import {FC, useEffect, useState} from 'react';
import { Link } from '@tanstack/react-router'
import {
    Modal, Box, Text,
    Flex, UnstyledButton, Divider, ActionIcon, HoverCard
} from '@mantine/core';
import { IconMinus, IconPlus, IconPrinter, IconEye, IconBrandWhatsapp } from '@tabler/icons-react';
import TemplateService from '@/services/template-service';
import PS from '@/services/permission/permission-service';
import * as Enums from '@/utils/enums';

interface PrintTemplate {
  text: string;
  link: string;
  customerZonePrintable: boolean;
}

interface PrintModalProps {
  printTemplates: PrintTemplate[];
  exportDocument: (endpoint: string, mode: 'view' | 'download') => void;
  onPrintLabel: (qty: number) => void;
  onClose: () => void;
  isPrinting: boolean;
  jobId: string | number;
  customerId: string | number;
  contact: any;
  jobIdentifier?: string;
}

const SectionLabel = ({ children }: { children: string }) => (
  <Text
    size="xs"
    fw={600}
    c="dimmed"
    mb={8}
    style={{ textTransform: 'uppercase', letterSpacing: '0.07em' }}
  >
    {children}
  </Text>
);

const PrintModal: FC<PrintModalProps> = ({
  printTemplates,
  exportDocument,
  onPrintLabel,
  onClose,
  isPrinting,
  jobId,
  customerId,
  contact,
  jobIdentifier,
}) => {
  const [labelQty, setLabelQty] = useState(0);
  const [activeLink, setActiveLink] = useState<string | null>(null);
  const [canViewDocumentSettings] = useState(PS.hasPermission(Enums.PermissionName.MasterOfficeAdmin));

    useEffect(() => {
        console.log(printTemplates);
    }, [printTemplates]);

  const handleWhatsAppClick = (template: PrintTemplate) => {
    if (!contact || !contact.MobileNumber) return;

    let documentType = '';
    if (template.link.includes('GetJobCustomerDocument')) {
      documentType = 'legacy';
    } else {
      const match = template.link.match(/documentType=([^&]+)/);
      if (match) {
        documentType = match[1];
      }
    }

    const message = TemplateService.getModuleMessageTemplate(
      Enums.Module.JobCard,
      jobId,
      customerId,
      contact.FirstName,
      jobIdentifier,
      undefined,
      documentType
    );

    let phone = contact.MobileNumber.replace(/\D/g, '');
    if (phone.startsWith('0')) {
      phone = '27' + phone.substring(1);
    }

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <Modal
      opened
      onClose={onClose}
      withCloseButton
      title={<Text size="md" fw={500}>Print documents</Text>}
      size={520}
      radius={4}
      styles={{
        content: { border: '0.5px solid var(--mantine-color-default-border)' },
        header: { borderBottom: '0.5px solid var(--mantine-color-default-border)', paddingBottom: 12 },
      }}
    >
      {/* Job Cards */}
      <Box pt="md" mb="lg">
        <SectionLabel>Job Cards</SectionLabel>

        <Flex direction="column" gap={8} mt={4}>
          {printTemplates.map((template, index) => (
            <UnstyledButton
              key={template.link}
              onClick={() => {
                if (isPrinting) return;
                setActiveLink(template.link);
                exportDocument(template.link, 'view');
              }}
              style={{
                border: '0.5px solid var(--mantine-color-default-border)',
                borderRadius: 8,
                padding: '12px 14px',
                cursor: isPrinting ? 'default' : 'pointer',
              }}
            >
              <Flex align="center" justify="space-between">
                <Text size="sm">{template.text}</Text>
                <Flex gap={6} onClick={(e) => e.stopPropagation()}>
                    <HoverCard disabled={contact?.MobileNumber && template.customerZonePrintable} position="bottom-start" withArrow>
                        <HoverCard.Target>
                            <span>
                                <ActionIcon
                                    bg={'transparent'}
                                    size="sm"
                                    variant="transparent"
                                    radius="sm"
                                    aria-label="WhatsApp"
                                    name={`wa-job-print-${index + 1}`}
                                    disabled={!contact?.MobileNumber || !template.customerZonePrintable}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleWhatsAppClick(template);
                                    }}
                                >
                                    <IconBrandWhatsapp size={16} color={(!contact?.MobileNumber || !template.customerZonePrintable) ? 'var(--mantine-color-gray-5)' : "#25D366"} />
                                </ActionIcon>
                            </span>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                            <Text size="sm">
                                {
                                    !contact?.MobileNumber ? <>There is no mobile number set for the customer contact</> : <>
                                        Not available in the Customer Zone <br />
                                        Change this in {canViewDocumentSettings ? (
                                        <Link style={{textDecoration: 'none', color: 'var(--mantine-color-scBlue-6)'}} to={'/settings/document/manage'}>Document Settings</Link>
                                    ) : (
                                        <Text span inherit fw={500}>Document Settings</Text>
                                    )}
                                    </>
                                }
                            </Text>
                        </HoverCard.Dropdown>
                    </HoverCard>

                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="scBlue"
                    radius="sm"
                    aria-label="View"
                    disabled={isPrinting}
                    onClick={(e) => {
                      e.stopPropagation();
                      exportDocument(template.link, 'view');
                    }}
                  >
                    <IconEye size={16} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="scBlue"
                    radius="sm"
                    aria-label="Print"
                    loading={isPrinting && activeLink === template.link}
                    disabled={isPrinting}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveLink(template.link);
                      exportDocument(template.link, 'download');
                    }}
                  >
                    <IconPrinter size={16} />
                  </ActionIcon>
                </Flex>
              </Flex>
            </UnstyledButton>
          ))}
        </Flex>
      </Box>

      {/* Labels */}
      <Divider />
      <Box pt="md" pb="md">
        <SectionLabel>Job Labels</SectionLabel>
        <Flex align="center" justify="space-between" mt="xs">
          <Text size="sm">How many labels do you need?</Text>

          <Flex align="center" gap={6}>
            <Flex align="center">
              <UnstyledButton
                onClick={() => setLabelQty((p: number) => Math.max(0, p - 1))}
                style={{
                  width: 30,
                  height: 30,
                  border: '0.5px solid var(--mantine-color-default-border)',
                  background: 'var(--mantine-color-default)',
                  borderRadius: '4px 0 0 4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <IconMinus size={12} />
              </UnstyledButton>

              <Box
                style={{
                  width: 40,
                  height: 30,
                  borderTop: '0.5px solid var(--mantine-color-default-border)',
                  borderBottom: '0.5px solid var(--mantine-color-default-border)',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {labelQty}
              </Box>

              <UnstyledButton
                onClick={() => setLabelQty((p: number) => Math.min(99, p + 1))}
                style={{
                  width: 30,
                  height: 30,
                  border: '0.5px solid var(--mantine-color-default-border)',
                  background: 'var(--mantine-color-default)',
                  borderRadius: '0 4px 4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <IconPlus size={12} />
              </UnstyledButton>
            </Flex>

            <ActionIcon
              variant="default"
              size="lg"
              radius="sm"
              aria-label="Print labels"
              loading={isPrinting && activeLink === 'labels'}
              disabled={labelQty === 0 || isPrinting}
              onClick={() => {
                setActiveLink('labels');
                onPrintLabel(labelQty);
              }}
            >
              <IconPrinter size={16} />
            </ActionIcon>
          </Flex>
        </Flex>
      </Box>
    </Modal>
  );
};

export default PrintModal;
